import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';
import * as Icons from '@mui/icons-material';
import { motion } from 'framer-motion';
import ModalLarge from './Base/ModalLarge';
import IconUploader from '../components/@generalComponents/IconUploader';
import {
  Box,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Divider,
  InputAdornment,
  Tooltip,
  Input,
  FormControl,
  InputLabel,
  Button,
  ListItemButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';
import { fontSize, fontWeight, width } from '@mui/system';
import BusinessInformationTag from '../components/@generalComponents/BusinessInformationTag';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';


const ModalEvolutionSheet = (props) => {
  const { t } = useTranslation();
  const {
    isOpen,
  } = props;

  const dispatch = useDispatch();
  const theme = useTheme();
  const isDarkmode = theme.palette.mode === 'dark';

  const businessPreference = useSelector((state) => state.core.businessData);
  const singleElementDetails = useSelector(
    (state) => state.element.singleElementDetails
  );

  const [expanded, setExpanded] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState({});
  const [specifications, setSpecifications] = useState({});
  const [photos, setPhotos] = useState({});
  const [notes, setNotes] = useState({});
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [currentElementId, setCurrentElementId] = useState(null);
  const [currentNote, setCurrentNote] = useState('');
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);

  const pdfRef = useRef();

  // Fetch reports when component mounts or targetProfileId changes
  useEffect(() => {
    const fetchReports = async () => {
      if (singleElementDetails?.targetProfileId) {
        try {
          const response = await nodeAxiosFirebase({
            t,
            method: 'GET',
            url: `chekup/evolution-sheet?targetProfileId=${singleElementDetails.targetProfileId}`,
          });
          setReports(response.data || []);
        } catch (error) {
          console.error('Error fetching reports:', error);
        }
      }
    };

    fetchReports();
  }, [singleElementDetails?.targetProfileId, t]);

  // Load report data when a report is selected
  useEffect(() => {
    if (selectedReportId) {
      const selectedReport = reports.find(report => report.id === selectedReportId);
      if (selectedReport) {
        setSelectedStatuses(selectedReport.statuses || {});
        setSpecifications(selectedReport.specifications || {});
        setPhotos(selectedReport.photos || {});
        setNotes(selectedReport.notes || {});
      }
    } else {
      // Reset all states when creating a new report
      setSelectedStatuses({});
      setSpecifications({});
      setPhotos({});
      setNotes({});
    }
  }, [selectedReportId, reports]);

  // Define boundaries for each element type
  const elementBoundaries = {
    // Brakes
    front_pads: { warning: 4, bad: 3 }, // mm
    rear_pads: { warning: 4, bad: 3 }, // mm
    front_discs: { warning: 22, bad: 20 }, // mm
    rear_discs: { warning: 22, bad: 20 }, // mm

    // Suspension
    front_shocks: { warning: 60, bad: 40 }, // %
    rear_shocks: { warning: 60, bad: 40 }, // %
    springs: { warning: 60, bad: 40 }, // %
    bearings: { warning: 0.5, bad: 1 }, // mm

    // Tires
    front_tires: { warning: 4, bad: 2 }, // 32nds
    rear_tires: { warning: 4, bad: 2 }, // 32nds
    tire_pressure: { warning: { min: 28, max: 36 }, bad: { min: 25, max: 40 } }, // PSI

    // Charging System
    alternator: { warning: { min: 13.2, max: 14.8 }, bad: { min: 12.8, max: 15.2 } }, // V
    battery: { warning: { min: 12.2, max: 12.8 }, bad: { min: 11.8, max: 13.2 } }, // V

    // Steering
    rack_and_pinion: { warning: 0.5, bad: 1 }, // mm
    ball_joints: { warning: 0.5, bad: 1 }, // mm
    tie_rods: { warning: 0.5, bad: 1 }, // mm

    // Exhaust
    muffler: { warning: 60, bad: 40 }, // %
    catalytic_converter: { warning: 60, bad: 40 }, // %
    particulate_filter: { warning: 60, bad: 40 }, // %

    // AC System
    compressor: { warning: { min: 1.5, max: 2.5 }, bad: { min: 1, max: 3 } }, // bar
    condenser: { warning: 60, bad: 40 }, // %
    cabin_filter: { warning: 60, bad: 40 }, // %
    refrigerant: { warning: 60, bad: 40 }, // %

    // Transmission
    clutch: { warning: 60, bad: 40 }, // %
    gearbox: { warning: 60, bad: 40 }, // %
    driveshaft: { warning: 0.5, bad: 1 }, // mm
    differential: { warning: 60, bad: 40 }, // %

    // Fluids
    engine_oil: { warning: 30, bad: 20 }, // %
    coolant: { warning: 30, bad: 20 }, // %
    washer_fluid: { warning: 30, bad: 20 }, // %
    transmission_fluid: { warning: 30, bad: 20 }, // %
    power_steering_fluid: { warning: 30, bad: 20 }, // %
  };

  const getStatusFromValue = (elementId, value) => {
    const boundaries = elementBoundaries[elementId];
    if (!boundaries || !value) return null;

    const numValue = parseFloat(value);

    // Handle range-based boundaries (min/max)
    if (boundaries.warning.min !== undefined) {
      if (numValue < boundaries.bad.min || numValue > boundaries.bad.max) return 'bad';
      if (numValue < boundaries.warning.min || numValue > boundaries.warning.max) return 'warning';
      return 'good';
    }

    // Handle threshold-based boundaries
    if (numValue <= boundaries.bad) return 'bad';
    if (numValue <= boundaries.warning) return 'warning';
    return 'good';
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleStatusChange = (elementId, status) => {
    setSelectedStatuses(prev => ({
      ...prev,
      [elementId]: status
    }));
  };

  const handleSpecificationChange = (elementId, value) => {
    setSpecifications(prev => ({
      ...prev,
      [elementId]: value
    }));

    // Automatically set status based on the value
    const status = getStatusFromValue(elementId, value);
    if (status) {
      setSelectedStatuses(prev => ({
        ...prev,
        [elementId]: status
      }));
    }
  };

  const handlePhotoUpload = (elementId, file) => {
    setPhotos(prev => ({
      ...prev,
      [elementId]: file
    }));
  };

  const handleNoteClick = (elementId) => {
    setCurrentElementId(elementId);
    setCurrentNote(notes[elementId] || '');
    setNoteModalOpen(true);
  };

  const handleNoteSave = () => {
    if (currentElementId) {
      setNotes(prev => ({
        ...prev,
        [currentElementId]: currentNote
      }));
    }
    setNoteModalOpen(false);
  };

  const handleNoteCancel = () => {
    setNoteModalOpen(false);
    setCurrentNote('');
    setCurrentElementId(null);
  };

  const categories = [
    {
      id: 'brakes',
      title: t('categoriesBrakes'),
      elements: [
        {
          id: 'front_pads',
          name: t('elementsFrontPads'),
          needsSpec: true,
          specLabel: t('specificationsWearMm'),
          description: t('descriptionsFrontPads')
        },
        {
          id: 'rear_pads',
          name: t('elementsRearPads'),
          needsSpec: true,
          specLabel: t('specificationsWearMm'),
          description: t('descriptionsRearPads')
        },
        {
          id: 'front_discs',
          name: t('elementsFrontDiscs'),
          needsSpec: true,
          specLabel: t('specificationsThicknessMm'),
          description: t('descriptionsFrontDiscs')
        },
        {
          id: 'rear_discs',
          name: t('elementsRearDiscs'),
          needsSpec: true,
          specLabel: t('specificationsThicknessMm'),
          description: t('descriptionsRearDiscs')
        },
        {
          id: 'brake_fluid',
          name: t('elementsBrakeFluid'),
          needsSpec: false,
          description: t('descriptionsBrakeFluid')
        },
        {
          id: 'brake_caliper',
          name: t('elementsBrakeCaliper'),
          needsSpec: false,
          description: t('descriptionsBrakeCaliper')
        },
        {
          id: 'brake_hoses',
          name: t('elementsBrakeHoses'),
          needsSpec: false,
          description: t('descriptionsBrakeHoses')
        }
      ]
    },
    {
      id: 'suspension',
      title: t('categoriesSuspension'),
      elements: [
        {
          id: 'front_shocks',
          name: t('elementsFrontShocks'),
          needsSpec: true,
          specLabel: t('specificationsConditionPercent'),
          description: t('descriptionsFrontShocks')
        },
        {
          id: 'rear_shocks',
          name: t('elementsRearShocks'),
          needsSpec: true,
          specLabel: t('specificationsConditionPercent'),
          description: t('descriptionsRearShocks')
        },
        {
          id: 'springs',
          name: t('elementsSprings'),
          needsSpec: true,
          specLabel: t('specificationsConditionPercent'),
          description: t('descriptionsSprings')
        },
        {
          id: 'bearings',
          name: t('elementsBearings'),
          needsSpec: true,
          specLabel: t('specificationsConditionPercent'),
          description: t('descriptionsBearings')
        },
        {
          id: 'tie_rods',
          name: t('elementsTieRods'),
          needsSpec: true,
          specLabel: t('specificationsPlayMm'),
          description: t('descriptionsTieRods')
        },
        {
          id: 'ball_joints',
          name: t('elementsBallJoints'),
          needsSpec: true,
          specLabel: t('specificationsPlayMm'),
          description: t('descriptionsBallJoints')
        }
      ]
    },
    {
      id: 'tires',
      title: t('categoriesTires'),
      elements: [
        {
          id: 'front_tires',
          name: t('elementsFrontTires'),
          needsSpec: true,
          specLabel: t('specificationsTread32nds'),
          description: t('descriptionsFrontTires')
        },
        {
          id: 'rear_tires',
          name: t('elementsRearTires'),
          needsSpec: true,
          specLabel: t('specificationsTread32nds'),
          description: t('descriptionsRearTires')
        },
        {
          id: 'tire_pressure',
          name: t('elementsTirePressure'),
          needsSpec: true,
          specLabel: t('specificationsPsi'),
          description: t('descriptionsTirePressure')
        },
        {
          id: 'wheel_balance',
          name: t('elementsWheelBalance'),
          needsSpec: false,
          description: t('descriptionsWheelBalance')
        },
        {
          id: 'valves',
          name: t('elementsValves'),
          needsSpec: false,
          description: t('descriptionsValves')
        },
        {
          id: 'wheels',
          name: t('elementsWheels'),
          needsSpec: false,
          description: t('descriptionsWheels')
        }
      ]
    },
    {
      id: 'charging_system',
      title: t('categoriesChargingSystem'),
      elements: [
        {
          id: 'alternator',
          name: t('elementsAlternator'),
          needsSpec: true,
          specLabel: t('specificationsVoltageV'),
          description: t('descriptionsAlternator')
        },
        {
          id: 'battery',
          name: t('elementsBattery'),
          needsSpec: true,
          specLabel: t('specificationsVoltageV'),
          description: t('descriptionsBattery')
        },
        {
          id: 'starter',
          name: t('elementsStarter'),
          needsSpec: false,
          description: t('descriptionsStarter')
        },
        {
          id: 'accessory_belt',
          name: t('elementsAccessoryBelt'),
          needsSpec: false,
          description: t('descriptionsAccessoryBelt')
        }
      ]
    },
    {
      id: 'steering',
      title: t('categoriesSteering'),
      elements: [
        {
          id: 'rack_and_pinion',
          name: t('elementsRackAndPinion'),
          needsSpec: true,
          specLabel: t('specificationsPlayMm'),
          description: t('descriptionsRackAndPinion')
        },
        {
          id: 'ball_joints',
          name: t('elementsBallJoints'),
          needsSpec: true,
          specLabel: t('specificationsPlayMm'),
          description: t('descriptionsBallJoints')
        },
        {
          id: 'tie_rods',
          name: t('elementsTieRods'),
          needsSpec: true,
          specLabel: t('specificationsPlayMm'),
          description: t('descriptionsTieRods')
        },
        {
          id: 'power_steering_fluid',
          name: t('elementsPowerSteeringFluid'),
          needsSpec: false,
          description: t('descriptionsPowerSteeringFluid')
        },
        {
          id: 'steering_column',
          name: t('elementsSteeringColumn'),
          needsSpec: false,
          description: t('descriptionsSteeringColumn')
        }
      ]
    },
    {
      id: 'exhaust',
      title: t('categoriesExhaust'),
      elements: [
        {
          id: 'muffler',
          name: t('elementsMuffler'),
          needsSpec: true,
          specLabel: t('specificationsConditionPercent'),
          description: t('descriptionsMuffler')
        },
        {
          id: 'catalytic_converter',
          name: t('elementsCatalyticConverter'),
          needsSpec: true,
          specLabel: t('specificationsConditionPercent'),
          description: t('descriptionsCatalyticConverter')
        },
        {
          id: 'particulate_filter',
          name: t('elementsParticulateFilter'),
          needsSpec: true,
          specLabel: t('specificationsConditionPercent'),
          description: t('descriptionsParticulateFilter')
        },
        {
          id: 'lambda_sensor',
          name: t('elementsLambdaSensor'),
          needsSpec: false,
          description: t('descriptionsLambdaSensor')
        },
        {
          id: 'exhaust_pipes',
          name: t('elementsExhaustPipes'),
          needsSpec: false,
          description: t('descriptionsExhaustPipes')
        }
      ]
    },
    {
      id: 'ac_system',
      title: t('categoriesAcSystem'),
      elements: [
        {
          id: 'compressor',
          name: t('elementsCompressor'),
          needsSpec: true,
          specLabel: t('specificationsPressureBar'),
          description: t('descriptionsCompressor')
        },
        {
          id: 'condenser',
          name: t('elementsCondenser'),
          needsSpec: true,
          specLabel: t('specificationsConditionPercent'),
          description: t('descriptionsCondenser')
        },
        {
          id: 'cabin_filter',
          name: t('elementsCabinFilter'),
          needsSpec: true,
          specLabel: t('specificationsConditionPercent'),
          description: t('descriptionsCabinFilter')
        },
        {
          id: 'refrigerant',
          name: t('elementsRefrigerant'),
          needsSpec: true,
          specLabel: t('specificationsLevelPercent'),
          description: t('descriptionsRefrigerant')
        },
        {
          id: 'evaporator',
          name: t('elementsEvaporator'),
          needsSpec: false,
          description: t('descriptionsEvaporator')
        },
        {
          id: 'blower_motor',
          name: t('elementsBlowerMotor'),
          needsSpec: false,
          description: t('descriptionsBlowerMotor')
        }
      ]
    },
    {
      id: 'transmission',
      title: t('categoriesTransmission'),
      elements: [
        {
          id: 'clutch',
          name: t('elementsClutch'),
          needsSpec: true,
          specLabel: t('specificationsWearPercent'),
          description: t('descriptionsClutch')
        },
        {
          id: 'gearbox',
          name: t('elementsGearbox'),
          needsSpec: true,
          specLabel: t('specificationsConditionPercent'),
          description: t('descriptionsGearbox')
        },
        {
          id: 'driveshaft',
          name: t('elementsDriveshaft'),
          needsSpec: true,
          specLabel: t('specificationsPlayMm'),
          description: t('descriptionsDriveshaft')
        },
        {
          id: 'differential',
          name: t('elementsDifferential'),
          needsSpec: true,
          specLabel: t('specificationsConditionPercent'),
          description: t('descriptionsDifferential')
        },
        {
          id: 'transmission_fluid',
          name: t('elementsTransmissionFluid'),
          needsSpec: true,
          specLabel: t('specificationsLevelPercent'),
          description: t('descriptionsTransmissionFluid')
        }
      ]
    },
    {
      id: 'fluids',
      title: t('categoriesFluids'),
      elements: [
        {
          id: 'engine_oil',
          name: t('elementsEngineOil'),
          needsSpec: true,
          specLabel: t('specificationsLevelPercent'),
          description: t('descriptionsEngineOil')
        },
        {
          id: 'coolant',
          name: t('elementsCoolant'),
          needsSpec: true,
          specLabel: t('specificationsLevelPercent'),
          description: t('descriptionsCoolant')
        },
        {
          id: 'washer_fluid',
          name: t('elementsWasherFluid'),
          needsSpec: true,
          specLabel: t('specificationsLevelPercent'),
          description: t('descriptionsWasherFluid')
        },
        {
          id: 'transmission_fluid',
          name: t('elementsTransmissionFluid'),
          needsSpec: true,
          specLabel: t('specificationsLevelPercent'),
          description: t('descriptionsTransmissionFluid')
        },
        {
          id: 'power_steering_fluid',
          name: t('elementsPowerSteeringFluid'),
          needsSpec: true,
          specLabel: t('specificationsLevelPercent'),
          description: t('descriptionsPowerSteeringFluid')
        }
      ]
    },
    {
      id: 'body',
      title: t('categoriesBody'),
      elements: [
        {
          id: 'paint',
          name: t('elementsPaint'),
          needsSpec: false,
          description: t('descriptionsPaint')
        },
        {
          id: 'windows',
          name: t('elementsWindows'),
          needsSpec: false,
          description: t('descriptionsWindows')
        },
        {
          id: 'headlights',
          name: t('elementsHeadlights'),
          needsSpec: false,
          description: t('descriptionsHeadlights')
        },
        {
          id: 'lights',
          name: t('elementsLights'),
          needsSpec: false,
          description: t('descriptionsLights')
        }
      ]
    }
  ];



  const handleSave = async () => {
    try {
      const body = {
        targetProfileId: singleElementDetails?.targetProfileId,
        targetId: singleElementDetails?.targetId,
        statuses: selectedStatuses,
        specifications: specifications,
        photos: photos,
        notes: notes
      };

      // If we have a selected report, add its ID to the body
      if (selectedReportId) {
        body.elementId = selectedReportId;
      }

      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `chekup/evolution-sheet`,
        body: body,
      });

      // If this was a new report, update the selected report ID
      if (!selectedReportId && response.data?.id) {
        setSelectedReportId(response.data.id);
        // Refresh the reports list
        const reportsResponse = await nodeAxiosFirebase({
          t,
          method: 'GET',
          url: `chekup/evolution-sheet?targetProfileId=${singleElementDetails.targetProfileId}`,
        });
        setReports(reportsResponse.data || []);
      }
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  const getStatusSummary = () => {
    const summary = {
      good: 0,
      warning: 0,
      bad: 0,
      total: 0
    };

    categories.forEach(category => {
      category.elements.forEach(element => {
        const status = selectedStatuses[element.id];
        if (status) {
          summary[status]++;
          summary.total++;
        }
      });
    });

    return summary;
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'good': return t('statusGood');
      case 'warning': return t('statusWarning');
      case 'bad': return t('statusBad');
      default: return t('statusNotChecked');
    }
  };

  const handlePrint = useReactToPrint({
    content: () => pdfRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 20mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  });

  const getClientFriendlyDescription = (elementId, status) => {
    const descriptions = {
      // Freins
      front_pads: {
        good: t('descriptionsFrontPadsGood'),
        warning: t('descriptionsFrontPadsWarning'),
        bad: t('descriptionsFrontPadsBad')
      },
      rear_pads: {
        good: t('descriptionsRearPadsGood'),
        warning: t('descriptionsRearPadsWarning'),
        bad: t('descriptionsRearPadsBad')
      },
      front_discs: {
        good: t('descriptionsFrontDiscsGood'),
        warning: t('descriptionsFrontDiscsWarning'),
        bad: t('descriptionsFrontDiscsBad')
      },
      rear_discs: {
        good: t('descriptionsRearDiscsGood'),
        warning: t('descriptionsRearDiscsWarning'),
        bad: t('descriptionsRearDiscsBad')
      },
      brake_fluid: {
        good: t('descriptionsBrakeFluidGood'),
        warning: t('descriptionsBrakeFluidWarning'),
        bad: t('descriptionsBrakeFluidBad')
      },
      brake_caliper: {
        good: t('descriptionsBrakeCaliperGood'),
        warning: t('descriptionsBrakeCaliperWarning'),
        bad: t('descriptionsBrakeCaliperBad')
      },
      brake_hoses: {
        good: t('descriptionsBrakeHosesGood'),
        warning: t('descriptionsBrakeHosesWarning'),
        bad: t('descriptionsBrakeHosesBad')
      },

      // Suspension
      front_shocks: {
        good: t('descriptionsFrontShocksGood'),
        warning: t('descriptionsFrontShocksWarning'),
        bad: t('descriptionsFrontShocksBad')
      },
      rear_shocks: {
        good: t('descriptionsRearShocksGood'),
        warning: t('descriptionsRearShocksWarning'),
        bad: t('descriptionsRearShocksBad')
      },
      springs: {
        good: t('descriptionsSpringsGood'),
        warning: t('descriptionsSpringsWarning'),
        bad: t('descriptionsSpringsBad')
      },
      bearings: {
        good: t('descriptionsBearingsGood'),
        warning: t('descriptionsBearingsWarning'),
        bad: t('descriptionsBearingsBad')
      },
      tie_rods: {
        good: t('descriptionsTieRodsGood'),
        warning: t('descriptionsTieRodsWarning'),
        bad: t('descriptionsTieRodsBad')
      },
      ball_joints: {
        good: t('descriptionsBallJointsGood'),
        warning: t('descriptionsBallJointsWarning'),
        bad: t('descriptionsBallJointsBad')
      },

      // Pneus
      front_tires: {
        good: t('descriptionsFrontTiresGood'),
        warning: t('descriptionsFrontTiresWarning'),
        bad: t('descriptionsFrontTiresBad')
      },
      rear_tires: {
        good: t('descriptionsRearTiresGood'),
        warning: t('descriptionsRearTiresWarning'),
        bad: t('descriptionsRearTiresBad')
      },
      tire_pressure: {
        good: t('descriptionsTirePressureGood'),
        warning: t('descriptionsTirePressureWarning'),
        bad: t('descriptionsTirePressureBad')
      },
      wheel_balance: {
        good: t('descriptionsWheelBalanceGood'),
        warning: t('descriptionsWheelBalanceWarning'),
        bad: t('descriptionsWheelBalanceBad')
      },
      valves: {
        good: t('descriptionsValvesGood'),
        warning: t('descriptionsValvesWarning'),
        bad: t('descriptionsValvesBad')
      },
      wheels: {
        good: t('descriptionsWheelsGood'),
        warning: t('descriptionsWheelsWarning'),
        bad: t('descriptionsWheelsBad')
      },

      // Système de charge
      alternator: {
        good: t('descriptionsAlternatorGood'),
        warning: t('descriptionsAlternatorWarning'),
        bad: t('descriptionsAlternatorBad')
      },
      battery: {
        good: t('descriptionsBatteryGood'),
        warning: t('descriptionsBatteryWarning'),
        bad: t('descriptionsBatteryBad')
      },
      starter: {
        good: t('descriptionsStarterGood'),
        warning: t('descriptionsStarterWarning'),
        bad: t('descriptionsStarterBad')
      },
      accessory_belt: {
        good: t('descriptionsAccessoryBeltGood'),
        warning: t('descriptionsAccessoryBeltWarning'),
        bad: t('descriptionsAccessoryBeltBad')
      },

      // Direction
      rack_and_pinion: {
        good: t('descriptionsRackAndPinionGood'),
        warning: t('descriptionsRackAndPinionWarning'),
        bad: t('descriptionsRackAndPinionBad')
      },
      power_steering_fluid: {
        good: t('descriptionsPowerSteeringFluidGood'),
        warning: t('descriptionsPowerSteeringFluidWarning'),
        bad: t('descriptionsPowerSteeringFluidBad')
      },
      steering_column: {
        good: t('descriptionsSteeringColumnGood'),
        warning: t('descriptionsSteeringColumnWarning'),
        bad: t('descriptionsSteeringColumnBad')
      },

      // Échappement
      muffler: {
        good: t('descriptionsMufflerGood'),
        warning: t('descriptionsMufflerWarning'),
        bad: t('descriptionsMufflerBad')
      },
      catalytic_converter: {
        good: t('descriptionsCatalyticConverterGood'),
        warning: t('descriptionsCatalyticConverterWarning'),
        bad: t('descriptionsCatalyticConverterBad')
      },
      particulate_filter: {
        good: t('descriptionsParticulateFilterGood'),
        warning: t('descriptionsParticulateFilterWarning'),
        bad: t('descriptionsParticulateFilterBad')
      },
      lambda_sensor: {
        good: t('descriptionsLambdaSensorGood'),
        warning: t('descriptionsLambdaSensorWarning'),
        bad: t('descriptionsLambdaSensorBad')
      },
      exhaust_pipes: {
        good: t('descriptionsExhaustPipesGood'),
        warning: t('descriptionsExhaustPipesWarning'),
        bad: t('descriptionsExhaustPipesBad')
      },

      // Climatisation
      compressor: {
        good: t('descriptionsCompressorGood'),
        warning: t('descriptionsCompressorWarning'),
        bad: t('descriptionsCompressorBad')
      },
      condenser: {
        good: t('descriptionsCondenserGood'),
        warning: t('descriptionsCondenserWarning'),
        bad: t('descriptionsCondenserBad')
      },
      cabin_filter: {
        good: t('descriptionsCabinFilterGood'),
        warning: t('descriptionsCabinFilterWarning'),
        bad: t('descriptionsCabinFilterBad')
      },
      refrigerant: {
        good: t('descriptionsRefrigerantGood'),
        warning: t('descriptionsRefrigerantWarning'),
        bad: t('descriptionsRefrigerantBad')
      },
      evaporator: {
        good: t('descriptionsEvaporatorGood'),
        warning: t('descriptionsEvaporatorWarning'),
        bad: t('descriptionsEvaporatorBad')
      },
      blower_motor: {
        good: t('descriptionsBlowerMotorGood'),
        warning: t('descriptionsBlowerMotorWarning'),
        bad: t('descriptionsBlowerMotorBad')
      },

      // Transmission
      clutch: {
        good: t('descriptionsClutchGood'),
        warning: t('descriptionsClutchWarning'),
        bad: t('descriptionsClutchBad')
      },
      gearbox: {
        good: t('descriptionsGearboxGood'),
        warning: t('descriptionsGearboxWarning'),
        bad: t('descriptionsGearboxBad')
      },
      driveshaft: {
        good: t('descriptionsDriveshaftGood'),
        warning: t('descriptionsDriveshaftWarning'),
        bad: t('descriptionsDriveshaftBad')
      },
      differential: {
        good: t('descriptionsDifferentialGood'),
        warning: t('descriptionsDifferentialWarning'),
        bad: t('descriptionsDifferentialBad')
      },
      transmission_fluid: {
        good: t('descriptionsTransmissionFluidGood'),
        warning: t('descriptionsTransmissionFluidWarning'),
        bad: t('descriptionsTransmissionFluidBad')
      },

      // Liquides
      engine_oil: {
        good: t('descriptionsEngineOilGood'),
        warning: t('descriptionsEngineOilWarning'),
        bad: t('descriptionsEngineOilBad')
      },
      coolant: {
        good: t('descriptionsCoolantGood'),
        warning: t('descriptionsCoolantWarning'),
        bad: t('descriptionsCoolantBad')
      },
      washer_fluid: {
        good: t('descriptionsWasherFluidGood'),
        warning: t('descriptionsWasherFluidWarning'),
        bad: t('descriptionsWasherFluidBad')
      },

      // Carrosserie
      paint: {
        good: t('descriptionsPaintGood'),
        warning: t('descriptionsPaintWarning'),
        bad: t('descriptionsPaintBad')
      },
      windows: {
        good: t('descriptionsWindowsGood'),
        warning: t('descriptionsWindowsWarning'),
        bad: t('descriptionsWindowsBad')
      },
      headlights: {
        good: t('descriptionsHeadlightsGood'),
        warning: t('descriptionsHeadlightsWarning'),
        bad: t('descriptionsHeadlightsBad')
      },
      lights: {
        good: t('descriptionsLightsGood'),
        warning: t('descriptionsLightsWarning'),
        bad: t('descriptionsLightsBad')
      }
    };

    return descriptions[elementId]?.[status] || t('descriptionNotAvailable');
  };

  const PDFContent = () => {
    const groupedElements = categories.reduce((acc, category) => {
      category.elements.forEach(element => {
        const status = selectedStatuses[element.id];
        if (status) {
          if (!acc[status]) {
            acc[status] = [];
          }
          acc[status].push({
            ...element,
            category: category.title,
            spec: specifications[element.id],
            photo: photos[element.id],
            note: notes[element.id]
          });
        }
      });
      return acc;
    }, {});

    return (
      <Box ref={pdfRef} sx={{ p: 3, maxWidth: '210mm', margin: '0 auto', backgroundColor: '#fff' }}>
        <Box sx={{
          mb: 3,
          borderBottom: '2px solid',
          borderColor: businessPreference?.mainColor,
          pb: 2
        }}>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
            backgroundColor: '#fffffff',
            p: 2,
            borderRadius: '6px'
          }}>

            <Box>
              <BusinessInformationTag businessData={businessPreference} />
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <img
                src="/assets/chekup/chekup-logo.png"
                alt="logo"
                style={{
                  height: '20px',
                  marginRight: '13px',
                  marginTop: '30px'
                }}
              />
              <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold', textAlign: 'right' }}>
                {t('vehicleInspectionReport')}
              </Typography>

            </Box>
          </Box>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
            backgroundColor: '#f5f5f5',
            p: 2,
            borderRadius: '6px'
          }}>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {singleElementDetails?.targetProfileDetails?.attribute1} {' '}
                {singleElementDetails?.targetProfileDetails?.attribute2} {' '}
                {singleElementDetails?.targetProfileDetails?.attribute3}{' '}
                {singleElementDetails?.targetProfileDetails?.attribute4}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rapport réalisé le {moment().format('DD MMM YYYY')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '10px' }}>
                Préparé par {'Carl G.'}
              </Typography>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">
                {singleElementDetails?.targetProfileDetails?.attribute1 || ''} {' '}
                {singleElementDetails?.targetProfileDetails?.attribute2 || ''} {' '}
                {singleElementDetails?.targetProfileDetails?.attribute3 || ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {singleElementDetails?.targetProfileDetails?.name || ''}
              </Typography>
            </Box>

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, ml: 0.4 }}>
                {t('vehicleSpecifications')}
              </Typography>
              <Box sx={{ pl: 0.4 }}>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                    {singleElementDetails?.targetProfileDetails?.name || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                    {t('vin')}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                    {singleElementDetails?.targetProfileDetails?.metric || 'N/A'} km
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                    {t('mileage')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                    {singleElementDetails?.targetProfileDetails?.attribute1 || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                    {t('year')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, ml: 0.4 }}>
                {t('clientDetails')}
              </Typography>
              <Box sx={{ pl: 0.4 }}>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                    {singleElementDetails?.targetDetails?.name || singleElementDetails?.targetName || '-'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                    {t('name')}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                    {singleElementDetails?.targetDetails?.phone || singleElementDetails?.targetPhone || '-'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                    {t('phone')}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                    {singleElementDetails?.targetDetails?.email || singleElementDetails?.targetEmail || '-'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                    {t('email')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                    {singleElementDetails?.targetDetails?.address || singleElementDetails?.targetAddress || '-'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                    {t('address')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{
          mb: 3,
          p: 2,
          border: '1px solid #e0e0e0',
          borderRadius: '6px',
          backgroundColor: '#fff',
        }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
            {t('inspectionSummary')}
          </Typography>
          {(() => {
            const summary = getStatusSummary();
            return (
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{
                    textAlign: 'center',
                    p: 1.5,
                    bgcolor: '#e8f5e9',
                    borderRadius: 1,
                    border: '1px solid #c8e6c9'
                  }}>
                    <Typography variant="h4" color="success.main" sx={{ mb: 1 }}>
                      {summary.good}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                      {t('elementsInGoodCondition')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{
                    textAlign: 'center',
                    p: 1.5,
                    bgcolor: '#fff3e0',
                    borderRadius: 1,
                    border: '1px solid #ffe0b2'
                  }}>
                    <Typography variant="h4" color="warning.main" sx={{ mb: 1 }}>
                      {summary.warning}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ef6c00' }}>
                      {t('elementsNeedAttention')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{
                    textAlign: 'center',
                    p: 1.5,
                    bgcolor: '#ffebee',
                    borderRadius: 1,
                    border: '1px solid #ffcdd2'
                  }}>
                    <Typography variant="h4" color="error.main" sx={{ mb: 1 }}>
                      {summary.bad}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#c62828' }}>
                      {t('elementsNeedRepair')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            );
          })()}
        </Box>

        {(groupedElements.bad?.length > 0 || groupedElements.warning?.length > 0) && (
          <Box sx={{
            mb: 3,
            p: 2,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            bgcolor: '#f2f2f240',
          }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, borderBottom: '1px solid #696969', pb: 0.1, mb: 1.4 }}>
              {t('priorityActions')}
            </Typography>

            <div className='row'>
              {/* Elements to repair */}
              {groupedElements.bad?.length > 0 && (
                <div className='col-6'>
                  <Typography variant="subtitle1" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CancelIcon /> {t('immediateRepairs')}
                  </Typography>
                  {groupedElements.bad.map((element, index) => (
                    <Box key={index} sx={{
                      mb: 1,
                      pl: 2,
                      borderLeft: '3px solid #f44336',
                      backgroundColor: '#fff',
                      p: 1,
                      borderRadius: '0 4px 4px 0'
                    }}>
                      <div className='row'>
                        <div className='col-8'>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, borderBottom: '1px solid #e0e0e0' }}>
                            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 'bold', fontSize: '11px', width: '60%' }}>
                              {element.name} ({element.category})
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 0.9,
                              py: 0.2,
                              borderRadius: 1,
                              width: '40%'
                            }}>
                              <CancelIcon sx={{ color: 'error.main', fontSize: '13px' }} />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '10px',
                                  color: '#c62828',
                                  fontWeight: 600,
                                  lineHeight: '1'
                                }}
                              >
                                {getStatusText('bad')}
                              </Typography>
                            </Box>
                          </Box>
                          {element.needsSpec && element.spec && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '10px', fontWeight: 600 }}>
                              {element.specLabel}: {element.spec}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '9px', mb: 1 }}>
                            {getClientFriendlyDescription(element.id, 'bad')}
                          </Typography>
                          {element.note && (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '8px' }}>
                                {t('noteAdvised')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '10px' }}>
                                {element.note}
                              </Typography>
                            </Box>
                          )}
                        </div>
                        <div className='col-4 align-right'>
                          {element.photo && (
                            <Box sx={{ mt: 1, mb: 1 }}>
                              <img
                                src={element.photo.fileUrl}
                                alt={element.name}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100px',
                                  objectFit: 'contain',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '4px',
                                  padding: '10px'
                                }}
                              />
                            </Box>
                          )}
                        </div>
                      </div>
                    </Box>
                  ))}
                </div>
              )}

              {groupedElements.warning?.length > 0 && (
                <div className='col-6'>
                  <Typography variant="subtitle1" color="warning.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon /> {t('monitoringRequired')}
                  </Typography>
                  {groupedElements.warning.map((element, index) => (
                    <Box key={index} sx={{
                      mb: 1,
                      pl: 2,
                      borderLeft: '3px solid #ff9800',
                      backgroundColor: '#fff',
                      p: 1,
                      borderRadius: '0 4px 4px 0'
                    }}>
                      <div className='row'>
                        <div className='col-8'>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, borderBottom: '1px solid #e0e0e0' }}>
                            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 'bold', fontSize: '11px', width: '60%' }}>
                              {element.name} ({element.category})
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 0.9,
                              py: 0.2,
                              borderRadius: 1,
                              width: '40%'
                            }}>
                              <WarningIcon sx={{ color: 'warning.main', fontSize: '13px' }} />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '10px',
                                  color: '#ef6c00',
                                  fontWeight: 600,
                                  lineHeight: '1'
                                }}
                              >
                                {getStatusText('warning')}
                              </Typography>
                            </Box>
                          </Box>
                          {element.needsSpec && element.spec && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '10px', fontWeight: 600 }}>
                              {element.specLabel}: {element.spec}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '9px', mb: 1 }}>
                            {getClientFriendlyDescription(element.id, 'warning')}
                          </Typography>
                          {element.note && (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '8px' }}>
                                {t('noteAdvised')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '10px' }}>
                                {element.note}
                              </Typography>
                            </Box>
                          )}
                        </div>
                        <div className='col-4 align-right'>
                          {element.photo && (
                            <Box sx={{ mt: 1, mb: 1 }}>
                              <img
                                src={element.photo.fileUrl}
                                alt={element.name}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100px',
                                  objectFit: 'contain',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '4px',
                                  padding: '10px'
                                }}
                              />
                            </Box>
                          )}
                        </div>
                      </div>
                    </Box>
                  ))}
                </div>
              )}
            </div>
          </Box>
        )}

        <Box>
          {categories.map((category) => (
            <Box key={category.id} sx={{
              mb: 2,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'hidden'
            }}>
              <Box sx={{
                p: 1,
                backgroundColor: businessPreference?.mainColor,
                color: '#fff'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', pl: 2 }}>
                  {category.title}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                {category.elements.map((element) => {
                  const status = selectedStatuses[element.id];
                  const spec = specifications[element.id];
                  const photo = photos[element.id];
                  const note = notes[element.id];
                  return (
                    <Box key={element.id} sx={{
                      mb: 0.5,
                      p: 1,
                      backgroundColor: status ? '#f5f5f580' : 'transparent',
                      borderRadius: '6px',
                      '&:last-child': { mb: 0 }
                    }}>
                      <div className='row'>
                        <div className='col-8'>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, borderBottom: '1px solid #e0e0e0' }}>
                            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 'bold' }}>
                              {element.name}
                            </Typography>
                            {status && (
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 0.9,
                                py: 0.2,
                                borderRadius: 1
                              }}>
                                {status === 'good' && <CheckCircleIcon sx={{ color: 'success.main', fontSize: '13px' }} />}
                                {status === 'warning' && <WarningIcon sx={{ color: 'warning.main', fontSize: '13px' }} />}
                                {status === 'bad' && <CancelIcon sx={{ color: 'error.main', fontSize: '13px' }} />}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '10px',
                                    color: status === 'good' ? '#2e7d32' :
                                      status === 'warning' ? '#ef6c00' : '#c62828',
                                    fontWeight: 600
                                  }}
                                >
                                  {getStatusText(status)}
                                </Typography>
                              </Box>
                            )}

                          </Box>
                          {element.needsSpec && spec && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '10px' }}>
                              {element.specLabel}: {spec}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '10px' }}>
                            {status ? getClientFriendlyDescription(element.id, status) : element.description}
                          </Typography>
                        </div>
                        <div className='col-4 align-right'>
                          {photo && (
                            <Box sx={{ mt: 1, mb: 1 }}>
                              <img
                                src={photo.fileUrl}
                                alt={element.name}
                                style={{
                                  maxWidth: '200px',
                                  maxHeight: '150px',
                                  objectFit: 'contain',
                                  border: '1px solid #e0e0e0',
                                  padding: '10px',
                                  borderRadius: '4px'
                                }}
                              />
                            </Box>
                          )}
                          {note && (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '8px' }}>
                                {t('noteAdvised')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '10px' }}>
                                {note}
                              </Typography>
                            </Box>
                          )}
                        </div>
                      </div>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{
          mt: 3,
          pt: 2,
          borderTop: '2px solid #696969',
          textAlign: 'center'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('inspectionReportFooter')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            © {moment().year()} - Tous droits réservés
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <div>
      <ModalLarge
        title={t('evolutionSheet')}
        subTitle={t('360Overview')}
        isOpen={isOpen}
        maxWidth={'lg'}
        fullWidth={true}
        fullHeight={true}
        {...props}
        labels={{
          primary: t('save'),
          secondary: t('print'),
        }}
        handlePrim={handleSave}
        handleSec={handlePrint}
      >
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3} md={4}>
              <Paper elevation={0} sx={{ p: 2, height: '68vh', overflowY: 'auto', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                  {t('inspectionPoints')}
                </Typography>

                {/* Vehicle Details */}
                <Box sx={{ mt: 0 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, ml: 0.4, fontWeight: 600 }}>
                    {t('specifications')}
                  </Typography>
                  <Box sx={{ pl: 0.4 }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                        {singleElementDetails?.targetProfileDetails?.name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                        {t('vin')}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                        {singleElementDetails?.targetProfileDetails?.metric || 'N/A'} km
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                        {t('mileage')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                        {singleElementDetails?.targetProfileDetails?.attribute1 || 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                        {t('year')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Client Details */}
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', pb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, ml: 0.4, fontWeight: 600 }}>
                    {t('clientDetails')}
                  </Typography>
                  <Box sx={{ pl: 0.4 }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                        {singleElementDetails?.targetDetails?.name || singleElementDetails?.targetName || '-'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                        {t('name')}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                        {singleElementDetails?.targetDetails?.phone || singleElementDetails?.targetPhone || '-'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                        {t('phone')}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                        {singleElementDetails?.targetDetails?.email || singleElementDetails?.targetEmail || '-'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                        {t('email')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 500 }}>
                        {singleElementDetails?.targetDetails?.address || singleElementDetails?.targetAddress || '-'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '9px' }}>
                        {t('address')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Report Selection Dropdown */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, ml: 0.4 }}>
                    {t('selectReport')}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('reports')}</InputLabel>
                    <Select
                      value={selectedReportId || ''}
                      onChange={(e) => setSelectedReportId(e.target.value)}
                      label={t('reports')}
                    >
                      <MenuItem value="">
                        <em>{t('createNewReport')}</em>
                      </MenuItem>
                      {reports?.map((report) => (
                        <MenuItem key={report.id} value={report.id}>
                          {moment.unix(report?.timeStamp?._seconds || report?.timeStamp?.seconds).format('DD MMM YYYY HH:mm')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={9} md={8}  >
              <Paper elevation={0} sx={{ p: 2, backgroundColor: 'transparent', height: '68vh', overflowY: 'auto' }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600, fontSize: '14px' }}>
                  {t('inspectionPointsList')}
                </Typography>
                {categories.map((category) => (
                  <Accordion
                    key={category.id}
                    expanded={expanded === category.id}
                    onChange={handleAccordionChange(category.id)}
                    elevation={0}
                    sx={{ m: 0, p: 0, backgroundColor: 'transparent' }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                        }
                      }}
                    >
                      <Typography>{category.title}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {(() => {
                          const counts = category.elements.reduce((acc, element) => {
                            const status = selectedStatuses[element.id];
                            if (status) acc[status]++;
                            return acc;
                          }, { good: 0, warning: 0, bad: 0 });

                          return (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckCircleIcon sx={{ fontSize: '16px', color: counts.good > 0 ? 'success.main' : 'grey.400' }} />
                                <Typography sx={{
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  color: counts.good > 0 ? 'success.main' : 'grey.400'
                                }}>
                                  {counts.good}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <WarningIcon sx={{ fontSize: '16px', color: counts.warning > 0 ? 'warning.main' : 'grey.400' }} />
                                <Typography sx={{
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  color: counts.warning > 0 ? 'warning.main' : 'grey.400'
                                }}>
                                  {counts.warning}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CancelIcon sx={{ fontSize: '16px', color: counts.bad > 0 ? 'error.main' : 'grey.400' }} />
                                <Typography sx={{
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  color: counts.bad > 0 ? 'error.main' : 'grey.400'
                                }}>
                                  {counts.bad}
                                </Typography>
                              </Box>
                            </>
                          );
                        })()}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List>
                        {category.elements.map((element, index) => (
                          <React.Fragment key={element.id}>
                            <ListItem
                              dense
                              divider
                              sx={{ p: 0 }}

                            >
                              <ListItemText
                                sx={{ width: '45%' }}
                                primary={element.name}
                                primaryTypographyProps={{
                                  sx: {
                                    fontSize: '11px',
                                    fontWeight: 600,
                                  },
                                }}
                                secondary={
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2, maxWidth: '350px', fontSize: '9px' }}>
                                    {element.description}
                                  </Typography>
                                }
                              />
                              <ListItem sx={{ width: '6%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <IconUploader
                                  size="small"
                                  onComplete={(file) => handlePhotoUpload(element.id, file)}
                                  elementType="evolution_sheet"
                                  fieldType="media-single"
                                  fieldKey={element?.id}
                                  label={photos[element?.id] ? t('viewPhoto') : t('addPhoto')}
                                  value={photos[element?.id]}
                                />
                              </ListItem>
                              <ListItem sx={{ width: '6%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative' }} onClick={() => handleNoteClick(element.id)}>
                                <Icons.EditNote />
                                {notes[element?.id] && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: '2px',
                                      right: '10px',
                                      width: '10px',
                                      height: '10px',
                                      borderRadius: '50%',
                                      backgroundColor: 'primary.main',
                                      border: '1px solid white',
                                    }}
                                  />
                                )}
                              </ListItem>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, width: '40%', textAlign: 'right', justifyContent: 'flex-end' }}>
                                {element.needsSpec && (
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      width: '200px',
                                      height: '26px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <input
                                      type="number"
                                      value={specifications[element.id] || ''}
                                      onChange={(e) => handleSpecificationChange(element.id, e.target.value)}
                                      style={{
                                        width: '100%',
                                        height: '26px',
                                        padding: '0 8px',
                                        fontSize: '10px',
                                        border: '1px solid #ccc',
                                        borderRadius: '8px',
                                        outline: 'none',
                                        backgroundColor: isDarkmode ? 'rgb(51,51,51)' : 'white',
                                        color: isDarkmode ? 'white' : 'black',
                                      }}
                                      onFocus={(e) => {
                                        e.target.style.borderColor = businessPreference?.mainColor || '#000';
                                        e.target.style.boxShadow = `0 0 0 0.2rem ${businessPreference?.mainColor + '20'}`;
                                      }}
                                      onBlur={(e) => {
                                        e.target.style.borderColor = '#ccc';
                                        e.target.style.boxShadow = 'none';
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        right: '8px',
                                        fontSize: '8px',
                                        color: isDarkmode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                                        pointerEvents: 'none',
                                      }}
                                    >
                                      {element.specLabel.split('(')[1]?.replace(')', '')}
                                    </Box>
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        left: '8px',
                                        top: '-10px',
                                        fontSize: '8px',
                                        backgroundColor: isDarkmode ? 'rgb(51,51,51)' : 'white',
                                        padding: '0 4px',
                                        color: isDarkmode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                                      }}
                                    >
                                      {element.specLabel}
                                    </Box>
                                  </Box>
                                )}
                                <Tooltip title="Bon état">
                                  <IconButton
                                    edge="end"
                                    onClick={() => handleStatusChange(element.id, 'good')}
                                    sx={{
                                      color: selectedStatuses[element.id] === 'good' ? 'success.main' : 'grey.400',
                                      '& .MuiSvgIcon-root': { fontSize: 28 }
                                    }}
                                  >
                                    <CheckCircleIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Attention requise">
                                  <IconButton
                                    edge="end"
                                    onClick={() => handleStatusChange(element.id, 'warning')}
                                    sx={{
                                      color: selectedStatuses[element.id] === 'warning' ? 'warning.main' : 'grey.400',
                                      '& .MuiSvgIcon-root': { fontSize: 28 }
                                    }}
                                  >
                                    <WarningIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Intervention nécessaire">
                                  <IconButton
                                    edge="end"
                                    onClick={() => handleStatusChange(element.id, 'bad')}
                                    sx={{
                                      color: selectedStatuses[element.id] === 'bad' ? 'error.main' : 'grey.400',
                                      '& .MuiSvgIcon-root': { fontSize: 28 }
                                    }}
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </ListItem>

                          </React.Fragment>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </ModalLarge>

      <div style={{ display: 'none' }}>
        <PDFContent />
      </div>

      <Dialog open={noteModalOpen} onClose={handleNoteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{t('addNote')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('note')}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={currentNote}
            onChange={(e) => setCurrentNote(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNoteCancel}>{t('cancel')}</Button>
          <Button onClick={handleNoteSave} variant="contained" color="primary">
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ModalEvolutionSheet;
